'use memo';
import { ReactNode, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@urbanx/agx.auth.client';
import { SetupNotificationsLayout, HubEventHandler } from 'layout/SetupNotificationsLayout';
import { HUB_NAMES } from 'constants/signalrConstants';
import InitialiseUser from 'services/functions/campaigns/initialiseUser';
import {
  NotificationType,
  CampaignUpdatedEvent,
  ContractUpdatedEvent,
  RexListingCreatedEvent,
  ContractStageUpdatedEvent,
  TaskUpdatedNotification,
  PdfProcessedEvent,
  TaskArrivalNotification,
  TaskAssigneeChangedNotification,
  TaskDeletedNotification,
  TaskCollectionChangedNotification,
  WorkflowConcurrencyNotification,
  ContractDocumentUpdatedNotification,
  AutomationFailureNotification,
} from 'types/pushNotifications';
import { emitTaskUpdate } from '../hooks/useAutopilotTaskUpdates';
import { handleCampaignUpdated, handleRexListingCreated } from './appNotificationsHandlers';
import { createCampaignDetailsQueryKey } from 'hooks/useSelectedCampaign';
import { emitPdfProcessedEvent } from 'hooks/usePdfProcessedEvent';
import {
  useInvalidateUseGetArchivedCampaignsByAdmin,
  useInvalidateUseGetCampaignsForAgency,
  useInvalidateUseStaffGetContract,
  useInvalidateUseStaffGetContractDeposits,
  useInvalidateUseStaffGetContractsForCampaign,
} from '@urbanx/agx.campaigns.client';
import { TaskCategoryTaskType, type AutopilotTaskDto } from '@urbanx/agx.activities.client';
import { useInvalidateUseGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { applyTaskUpdateToTasks } from 'utils/tasks/taskUpdates';
import {
  applyTaskUpdateToSearchData,
  removeTaskFromSearchData,
  restartTaskSearchData,
  taskSearchQueryRoot,
  taskUpdateRequiresSearchRestart,
  type TaskSearchData,
} from 'pages/allTasks/taskSearchQuery';
import { emitTaskArrival, emitTaskAssigneeChange } from 'hooks/useTaskArrivals';
import { getWorkflowConcurrencyToastId } from 'hooks/useWorkflowConcurrencyError';
import {
  invalidateActiveStaffContractQueries,
  invalidateCampaignQueriesExceptTasks,
  invalidateContractDateQueries,
  invalidateContractDetailQueries,
  refreshActiveTaskCollections,
  refreshContractStage,
  refreshTaskCollection,
} from './signalrCacheInvalidation';
import { useToast } from '@urbanx/agx.ui-components2';
import { emitAutomationFailureNotification } from 'hooks/useAutomationFailureNotifications';
import { emitAutomationFailureRefresh } from 'hooks/useAutomationFailureRefresh';
import { invalidateAutomationFailureQueries } from 'pages/automation-errors/automationFailureCache';
import { emitTaskCollectionReconnect } from 'hooks/taskCollectionRefresh';

interface SignalrCommsProps {
  children: ReactNode;
}

const handleAutopilotTasksConnected = () => console.log('AutopilotTasks hub connected');

const dashboardStatsQueryRoot = ['getDashboardStats'] as const;

const isAutopilotTasksQueryForCampaign = (queryKey: readonly unknown[], campaignId: string) =>
  queryKey[0] === 'getAutopilotTasksForCampaign' && queryKey[1] === campaignId;

const isAutopilotTasksQueryForWorkflow = (
  queryKey: readonly unknown[],
  event: WorkflowConcurrencyNotification
) =>
  isAutopilotTasksQueryForCampaign(queryKey, event.campaignId) && queryKey[2] === event.contractId;

const SignalrComms = ({ children }: SignalrCommsProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const invalidateCampaignListing = useInvalidateUseGetListingByCampaignId();
  const invalidateCampaigns = useInvalidateUseGetCampaignsForAgency();
  const invalidateArchivedCampaigns = useInvalidateUseGetArchivedCampaignsByAdmin();
  const invalidateSelectedContract = useInvalidateUseStaffGetContract();
  const invalidateContractDeposits = useInvalidateUseStaffGetContractDeposits();
  const invalidateContractsList = useInvalidateUseStaffGetContractsForCampaign();
  const refreshSelectedContract = (contractId: string, campaignId: string) =>
    Promise.all([
      invalidateSelectedContract(contractId),
      invalidateContractDetailQueries(queryClient, { contractId, campaignId }),
      invalidateContractsList(campaignId),
    ]);
  const invalidateSelectedCampaign = async (campaignId: string) => {
    await queryClient.invalidateQueries({
      queryKey: createCampaignDetailsQueryKey(campaignId),
      exact: true,
    });
  };
  const invalidateCampaignScopedQueries = async (campaignId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && query.queryKey.includes(campaignId),
      }),
      invalidateContractDetailQueries(queryClient),
    ]);
  };
  const handleTaskUpdated = (event: TaskUpdatedNotification) => {
    emitTaskUpdate(event);

    const campaignId = event.task.campaignId;
    if (!campaignId) return;

    if (
      event.task.contractId &&
      (event.task.category?.taskType === TaskCategoryTaskType.InitialDeposit ||
        event.task.category?.taskType === TaskCategoryTaskType.BalanceDeposit)
    ) {
      void invalidateContractDeposits(event.task.contractId);
    }

    queryClient.setQueriesData<AutopilotTaskDto[]>(
      {
        predicate: (query) => isAutopilotTasksQueryForCampaign(query.queryKey, campaignId),
      },
      (previousTasks) =>
        previousTasks ? applyTaskUpdateToTasks(previousTasks, event) : previousTasks
    );
    void invalidateContractDateQueries(queryClient, event);
    void queryClient.invalidateQueries({ queryKey: dashboardStatsQueryRoot });

    let searchRestartRequired = false;
    queryClient.setQueriesData<TaskSearchData>(
      { queryKey: taskSearchQueryRoot },
      (previousData) => {
        if (!previousData) return previousData;

        const shouldRestart = taskUpdateRequiresSearchRestart(previousData, event);
        const updatedData = applyTaskUpdateToSearchData(previousData, event);
        if (!shouldRestart) return updatedData;

        searchRestartRequired = true;
        return restartTaskSearchData(updatedData);
      }
    );

    if (searchRestartRequired) {
      void queryClient.invalidateQueries({ queryKey: taskSearchQueryRoot });
    }
  };
  const handleTaskCreated = async (event: TaskArrivalNotification) => {
    emitTaskArrival(event);

    const campaignId = event.campaignId;
    if (!campaignId) return;

    await Promise.all([
      queryClient.invalidateQueries({
        predicate: (query) => isAutopilotTasksQueryForCampaign(query.queryKey, campaignId),
      }),
      queryClient.invalidateQueries({ queryKey: dashboardStatsQueryRoot }),
    ]);
  };
  const handleTaskBecameVisible = (event: TaskArrivalNotification) => {
    emitTaskArrival(event);
    void queryClient.invalidateQueries({ queryKey: dashboardStatsQueryRoot });
  };
  const handleTaskAssigneeChanged = (event: TaskAssigneeChangedNotification) =>
    emitTaskAssigneeChange(event);
  const handleTaskDeleted = async (event: TaskDeletedNotification) => {
    queryClient.setQueriesData<TaskSearchData>({ queryKey: taskSearchQueryRoot }, (previousData) =>
      previousData
        ? restartTaskSearchData(removeTaskFromSearchData(previousData, event.taskId))
        : previousData
    );

    await Promise.all([
      queryClient.invalidateQueries({
        predicate: (query) => isAutopilotTasksQueryForCampaign(query.queryKey, event.campaignId),
      }),
      queryClient.invalidateQueries({ queryKey: dashboardStatsQueryRoot }),
      queryClient.invalidateQueries({ queryKey: taskSearchQueryRoot }),
    ]);
  };
  const handleWorkflowConcurrency = async (event: WorkflowConcurrencyNotification) => {
    const isViewingWorkflow = queryClient
      .getQueryCache()
      .findAll({
        predicate: (query) =>
          query.isActive() && isAutopilotTasksQueryForWorkflow(query.queryKey, event),
      })
      .some(() => true);

    await queryClient.invalidateQueries({
      predicate: (query) => isAutopilotTasksQueryForWorkflow(query.queryKey, event),
    });

    if (!isViewingWorkflow) return;

    pushToast(
      {
        type: 'error',
        message:
          "Tasks couldn't be updated after concurrent changes. Review the latest tasks and try again.",
      },
      getWorkflowConcurrencyToastId(event.campaignId, event.contractId)
    );
  };
  const handleAppNotificationsReconnected = async () => {
    emitAutomationFailureRefresh();
    await Promise.all([
      invalidateActiveStaffContractQueries(queryClient),
      invalidateAutomationFailureQueries(queryClient),
    ]);
  };
  const handleAutopilotTasksReconnected = async () => {
    emitTaskCollectionReconnect();
    await refreshActiveTaskCollections(queryClient);
  };
  const handleAppNotificationsConnected = () => {
    console.log('AppNotifications hub connected');
    InitialiseUser(getAuthToken);
  };

  const appNotificationHandlers: HubEventHandler[] = [
    {
      eventType: NotificationType.ContractUpdatedEvent,
      handler: async (event: ContractUpdatedEvent) => {
        if (!event.contractId?.trim()) return;

        await refreshSelectedContract(event.contractId, event.campaignId);
      },
    },
    {
      eventType: NotificationType.CampaignUpdatedEvent,
      handler: (event: CampaignUpdatedEvent) =>
        handleCampaignUpdated(event, invalidateCampaignScopedQueries, invalidateSelectedCampaign, [
          invalidateCampaigns,
          invalidateArchivedCampaigns,
        ]),
    },
    {
      eventType: NotificationType.PdfProcessedEvent,
      handler: (event: PdfProcessedEvent) => emitPdfProcessedEvent(event),
    },
    {
      eventType: NotificationType.RexListingCreatedEvent,
      handler: (event: RexListingCreatedEvent) =>
        handleRexListingCreated(event, invalidateCampaignListing),
    },
    {
      eventType: NotificationType.ContractStageUpdatedEvent,
      handler: (event: ContractStageUpdatedEvent) =>
        refreshContractStage(event, refreshSelectedContract, (campaignId) =>
          invalidateCampaignQueriesExceptTasks(queryClient, campaignId)
        ),
    },
    {
      eventType: NotificationType.AutomationFailureNotification,
      handler: async (event: AutomationFailureNotification) => {
        emitAutomationFailureNotification(event);
        await invalidateAutomationFailureQueries(queryClient, event.reference);
      },
    },
  ];

  const autopilotTaskHandlers: HubEventHandler[] = [
    {
      eventType: NotificationType.TaskUpdatedNotification,
      handler: (event: TaskUpdatedNotification) => handleTaskUpdated(event),
    },
    {
      eventType: NotificationType.TaskCreatedNotification,
      handler: (event: TaskArrivalNotification) => handleTaskCreated(event),
    },
    {
      eventType: NotificationType.TaskBecameVisibleNotification,
      handler: (event: TaskArrivalNotification) => handleTaskBecameVisible(event),
    },
    {
      eventType: NotificationType.TaskAssigneeChangedNotification,
      handler: (event: TaskAssigneeChangedNotification) => handleTaskAssigneeChanged(event),
    },
    {
      eventType: NotificationType.TaskDeletedNotification,
      handler: (event: TaskDeletedNotification) => handleTaskDeleted(event),
    },
    {
      eventType: NotificationType.TaskCollectionChangedNotification,
      handler: (event: TaskCollectionChangedNotification) =>
        refreshTaskCollection(queryClient, event),
    },
    {
      eventType: NotificationType.WorkflowConcurrencyNotification,
      handler: (event: WorkflowConcurrencyNotification) => handleWorkflowConcurrency(event),
    },
    {
      eventType: NotificationType.ContractDocumentUpdatedNotification,
      handler: async (event: ContractDocumentUpdatedNotification) => {
        if (!event.campaignId) return;

        await Promise.all([
          invalidateCampaignScopedQueries(event.campaignId),
          invalidateSelectedCampaign(event.campaignId),
        ]);
      },
    },
  ];
  return (
    <SetupNotificationsLayout
      hubName={HUB_NAMES.APP_NOTIFICATIONS}
      eventHandlers={appNotificationHandlers}
      onConnected={handleAppNotificationsConnected}
      onReconnected={handleAppNotificationsReconnected}
    >
      <SetupNotificationsLayout
        hubName={HUB_NAMES.AUTOPILOT_TASKS}
        eventHandlers={autopilotTaskHandlers}
        onConnected={handleAutopilotTasksConnected}
        onReconnected={handleAutopilotTasksReconnected}
      >
        {children}
      </SetupNotificationsLayout>
    </SetupNotificationsLayout>
  );
};

export default SignalrComms;
